import type { MonitoringLogEntry, LogLevel } from '@/lib/services/analytics';
import { loadAnalyticsService } from '@/lib/services/analytics-loader';

type LoggableContext = Record<string, unknown> | undefined;

const LOG_ENDPOINT = '/api/logs';

/**
 * Centralized Client Logger
 *
 * Features:
 * - Automatic error/warning capture
 * - Server-side log persistence
 * - Analytics integration
 * - Production-ready error handling
 */
class ClientLogger {
  private initialized = false;
  private originalConsoleError?: (...args: unknown[]) => void;
  private originalConsoleWarn?: (...args: unknown[]) => void;

  init() {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }
    this.initialized = true;

    this.originalConsoleError = console.error.bind(console);
    this.originalConsoleWarn = console.warn.bind(console);

    console.error = (...args: unknown[]) => {
      this.originalConsoleError?.(...args);
      const [first, ...rest] = args;
      this.error(first, { console: this.prepareArgs(rest), source: 'console.error' });
    };

    console.warn = (...args: unknown[]) => {
      this.originalConsoleWarn?.(...args);
      const [first, ...rest] = args;
      this.warn(first ?? 'Console warning', { console: this.prepareArgs(rest), source: 'console.warn' });
    };

    window.addEventListener('error', this.handleErrorEvent, { capture: true });
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  info(message: unknown, context?: LoggableContext) {
    this.log('info', this.stringifyMessage(message), { context });
  }

  warn(message: unknown, context?: LoggableContext) {
    this.log('warn', this.stringifyMessage(message), { context });
  }

  error(error: unknown, context?: LoggableContext) {
    const normalized = this.normalizeError(error);
    this.log('error', normalized.message, { stack: normalized.stack, context: { ...context, errorMeta: normalized.meta } });
  }

  private handleErrorEvent = (event: ErrorEvent) => {
    this.error(event.error ?? event.message, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    this.error(event.reason ?? 'Unhandled promise rejection', {
      source: 'unhandledrejection'
    });
  };

  private log(level: LogLevel, message: string, extra?: { stack?: string; context?: LoggableContext }) {
    const entry = this.createEntry(level, message, extra);
    void loadAnalyticsService()
      .then(async analytics => {
        await analytics.recordMonitoringLog(entry);
      })
      .catch(err => {
        this.originalConsoleWarn?.('Failed to persist monitoring log', err);
      });
    this.sendToServer(entry);
  }

  private createEntry(level: LogLevel, message: string, extra?: { stack?: string; context?: LoggableContext }): MonitoringLogEntry {
    const now = Date.now();
    return {
      id: `${now}-${Math.random().toString(36).slice(2, 10)}`,
      level,
      message,
      timestamp: now,
      url: this.safeLocationHref(),
      path: this.safeLocationPath(),
      stack: extra?.stack,
      userAgent: this.safeUserAgent(),
      context: this.sanitizeContext(extra?.context)
    };
  }

  private sanitizeContext(context: LoggableContext): MonitoringLogEntry['context'] {
    if (!context) {
      return undefined;
    }

    try {
      return JSON.parse(JSON.stringify(context, (key, value) => {
        if (value instanceof Error) {
          return { message: value.message, stack: value.stack };
        }
        if (typeof value === 'bigint') {
          return value.toString();
        }
        if (typeof value === 'function') {
          return value.name || 'anonymous';
        }
        return value;
      }));
    } catch {
      return 'Unserializable context';
    }
  }

  private prepareArgs(args: unknown[]): unknown[] | undefined {
    if (!args.length) {
      return undefined;
    }
    return args.map(arg => {
      if (arg instanceof Error) {
        return { message: arg.message, stack: arg.stack };
      }
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.parse(JSON.stringify(arg));
        } catch {
          return String(arg);
        }
      }
      return arg;
    });
  }

  private normalizeError(error: unknown): { message: string; stack?: string; meta?: Record<string, unknown> } {
    if (error instanceof Error) {
      return {
        message: error.message,
        stack: error.stack,
        meta: {
          name: error.name
        }
      };
    }

    if (typeof error === 'string') {
      return { message: error };
    }

    try {
      return {
        message: JSON.stringify(error),
        meta: { raw: error }
      };
    } catch {
      return {
        message: String(error),
        meta: { raw: String(error) }
      };
    }
  }

  private stringifyMessage(message: unknown): string {
    if (typeof message === 'string') {
      return message;
    }
    if (message instanceof Error) {
      return message.message;
    }
    try {
      return JSON.stringify(message);
    } catch {
      return String(message);
    }
  }

  private safeLocationHref(): string | undefined {
    try {
      return typeof window !== 'undefined' ? window.location.href : undefined;
    } catch {
      return undefined;
    }
  }

  private safeLocationPath(): string | undefined {
    try {
      return typeof window !== 'undefined' ? window.location.pathname : undefined;
    } catch {
      return undefined;
    }
  }

  private safeUserAgent(): string | undefined {
    try {
      return typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    } catch {
      return undefined;
    }
  }

  private sendToServer(entry: MonitoringLogEntry) {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const payload = JSON.stringify({ entries: [entry], source: 'client' });
        const blob = new Blob([payload], { type: 'application/json' });
        const ok = navigator.sendBeacon(LOG_ENDPOINT, blob);
        if (ok) {
          return;
        }
      } catch (error) {
        this.originalConsoleWarn?.('navigator.sendBeacon failed', error);
      }
    }

    if (typeof fetch === 'function') {
      fetch(LOG_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: [entry], source: 'client' }),
        keepalive: true
      }).catch(error => {
        this.originalConsoleWarn?.('Log transmission failed', error);
      });
    }
  }

  /**
   * Reset logger state (for testing)
   */
  reset() {
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }
    if (this.originalConsoleWarn) {
      console.warn = this.originalConsoleWarn;
    }
    this.initialized = false;
    this.originalConsoleError = undefined;
    this.originalConsoleWarn = undefined;

    if (typeof window !== 'undefined') {
      window.removeEventListener('error', this.handleErrorEvent);
      window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
  }
}

export const logger = new ClientLogger();

export const initializeMonitoring = () => {
  logger.init();
};
